import { useState, useEffect } from 'react'

import { updateEventFeed } from '_services/data-explorer'
import { logErrorAsync } from '_lib/utility.js'
import { useEventFeedsContext } from '../../context/index.jsx'

const INITIAL_STATE = []

const FILTER_SCHEMA = { key: '', operator: '', value: '' }

export const MULTI_VALUE_OPERATORS = [
  'and',
  'in',
  'similar_to',
  'not_in',
  'not_similar_to',
  'equals',
  'substring_in',
  'regex',
]

export const FILE_OPERATORS = ['in_file', 'not_in_file', 'similar_to_file']
export const SIMILAR_ATTRIBUTES = ['keywords', 'kecc']
export const SIMILAR_OPERATORS = ['similar_to', 'in']

const ensureSchema = (newValue, schema, name, oldValue, options = {}) => {
  if (name === 'value') {
    // options carry what the value was uploaded with, e.g. hasHeader for files
    return { ...newValue, ...options }
  }

  // "has header row" only describes a file, so it goes with the file operators
  const keepsHasHeader = FILE_OPERATORS.includes(newValue.operator)

  if (schema.filters[newValue.key]?.operators.includes(newValue.operator)) {
    const oldOperatorIsMultiValue = MULTI_VALUE_OPERATORS.includes(oldValue?.operator)
    const newOperatorIsMultiValue = MULTI_VALUE_OPERATORS.includes(newValue.operator)
    //different types of operators
    if (oldOperatorIsMultiValue !== newOperatorIsMultiValue) {
      return { ...newValue, value: undefined, hasHeader: undefined }
    }
    return keepsHasHeader ? newValue : { ...newValue, hasHeader: undefined }
  }

  return {
    ...newValue,
    operator: schema.filters[newValue.key]?.operators[0],
    value: undefined,
    hasHeader: undefined,
    ...options,
  }
}

const useFiltersCRUD = ({ onSave = () => {}, saveOnDelete = false }) => {
  const { data, dispatch, ACTIONS, schema, id: feedId } = useEventFeedsContext()
  const [filters, setFilters] = useState(INITIAL_STATE)
  const [selectedTabIndex, setSelectedTabIndex] = useState(0)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    setFilters(data.filters)
  }, [data])

  const handleSaveChanges = async (newFilters) => {
    setIsLoading(true)

    const newData = {
      ...data,
      filters: Array.isArray(newFilters)
        ? newFilters
        : filters.filter((filter) => !(filter.key.trim() === '')),
    }

    try {
      const response = feedId !== 'discovery' ? await updateEventFeed(feedId, newData) : newData
      dispatch({
        type: ACTIONS.SET_DATA,
        payload: response,
      })
      toast('Changes saved')
      onSave()
    } catch (error) {
      logErrorAsync(error)
      toast.error(error.message)
    }

    setIsLoading(false)
  }

  const handleAddFilter = () => {
    if (filters.some((val) => val.key === '')) {
      setSelectedTabIndex(filters.findIndex((val) => val.key === ''))
      return
    }
    setFilters((filters) => [...filters, FILTER_SCHEMA])
    setSelectedTabIndex(filters.length)
  }

  const handleEditFilter = (id, name, value, options) => {
    setFilters((filters) => {
      const newState = filters.map((filter, index) =>
        index === id
          ? ensureSchema({ ...filter, [name]: value }, schema, name, filter, options)
          : filter
      )
      return newState
    })
  }

  const handleRemoveFilter = (id) => {
    setFilters((prevState) => {
      const newState = prevState.filter((filter, index) => !(index === id))
      if (saveOnDelete) {
        handleSaveChanges(newState)
      }
      return newState
    })
  }

  const handleSelectFilter = (event) => {
    setSelectedTabIndex(Number(event.currentTarget.value))
  }

  return {
    handleSaveChanges,
    handleAddFilter,
    handleEditFilter,
    handleRemoveFilter,
    handleSelectFilter,
    selectedTabIndex,
    isLoading,
    filters,
  }
}

export default useFiltersCRUD
